import { ApiError, type DriverShipment, type OtpStatus } from "@khazzin/api-client";
import { Icon } from "@khazzin/ui/icons/Icon";
import { NumericPad, SignaturePad } from "@khazzin/ui/patterns";
import { CameraCapture } from "@khazzin/ui/patterns/CameraCapture";
import { Button, ConfirmDialog, Drawer, Field, IconButton, Input } from "@khazzin/ui/primitives";
import { CheckCircle2, Circle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useApi } from "../../api";
import { useI18n } from "../../i18n";
import { useOffline } from "../../offline/Offline";
import {
	checklist,
	done,
	MAX_PHOTOS,
	maskPhone,
	type PodDraft,
	podCaptured,
	RESEND_AFTER_S,
	satisfied,
} from "./pod";

/**
 * The proof of delivery step (DA-10-18, EP10-S18), after Delivered or a
 * partial's Continue: a checklist of what the store requires, each ticked as
 * its evidence is captured — photos (up to three), the recipient's signature
 * and name, the recipient's one-time code — and **Complete delivery**, enabled
 * only when the required set is met.
 *
 * Photos and the signature are captured with no signal and wait on the phone;
 * the delivery completes locally and syncs, and the files upload after it.
 * The code needs a signal (the server sends and matches it); without one, or
 * after five wrong codes, the fallback is a photo and the name of who took the
 * parcel, which the server accepts in the code's place (`03/06` §3).
 *
 * Every change is checkpointed by the caller, so a phone killed mid-step
 * reopens here with what was captured.
 */
export interface PodStepProps {
	stop: DriverShipment;
	draft: PodDraft;
	photos: Blob[];
	signature: Blob | null;
	onChange: (next: PodDraft) => void;
	onPhoto: (photo: Blob) => void;
	onRemovePhoto: (index: number) => void;
	onSignature: (png: Blob | null) => void;
	onCancel: () => void;
	onComplete: () => Promise<void>;
}

/** An `OtpStatus` refusal's message key. */
function otpError(error: unknown, status: OtpStatus | null): { key: string; tries?: number } {
	const code = error instanceof ApiError ? error.code : "";
	switch (code) {
		case "otp_invalid":
			return { key: "driver.pod.otp.invalid", tries: status?.triesLeft ?? 0 };
		case "otp_locked":
			return { key: "driver.pod.otp.locked" };
		case "otp_expired":
			return { key: "driver.pod.otp.expired" };
		case "otp_send_limit":
			return { key: "driver.pod.otp.sendLimit" };
		case "recipient_phone_missing":
			return { key: "driver.pod.otp.noPhone" };
		default:
			return { key: "driver.pod.otp.failed" };
	}
}

export function PodStep({
	stop,
	draft,
	photos,
	signature,
	onChange,
	onPhoto,
	onRemovePhoto,
	onSignature,
	onCancel,
	onComplete,
}: PodStepProps) {
	const { t } = useI18n();
	const [busy, setBusy] = useState(false);
	// Cancel asks as the back arrow does (ADJ-M03-4).
	const [asking, setAsking] = useState(false);
	const required = stop.podRequiredMethods ?? [];
	const methods = checklist(required);
	const wantsPhoto = methods.includes("photo") || draft.fallback;

	async function complete() {
		setBusy(true);
		try {
			await onComplete();
		} finally {
			setBusy(false);
		}
	}

	return (
		<Drawer
			open
			onOpenChange={(open) => {
				if (!open) {
					onCancel();
				}
			}}
			title={t("driver.pod.title")}
			description={t(required.length === 0 ? "driver.pod.defaultRule" : "driver.pod.storeRule")}
			// ADJ-M03-4: a stray back or Escape must not throw away the photos,
			// the signature or a verified code.
			dirty={podCaptured(draft)}
			discard={{
				title: t("driver.outcome.discardTitle"),
				description: t("driver.pod.discard"),
				confirmLabel: t("driver.outcome.discard"),
				cancelLabel: t("driver.outcome.keepEditing"),
			}}
			closeLabel={t("driver.outcome.cancel")}
			backLabel={t("driver.outcome.cancel")}
			footer={
				<>
					<Button onClick={() => (podCaptured(draft) ? setAsking(true) : onCancel())}>
						{t("driver.outcome.cancel")}
					</Button>
					<Button
						variant="primary"
						size="lg"
						disabled={!satisfied(required, draft)}
						loading={busy}
						onClick={() => void complete()}
					>
						{t("driver.pod.complete")}
					</Button>
				</>
			}
		>
			<div className="flex flex-col gap-5">
				<ul aria-label={t("driver.pod.checklist")} className="flex flex-col gap-1">
					{methods.map((m) => {
						const ok = done(m, draft);
						return (
							<li key={m} className="flex min-h-10 items-center gap-2 text-[15px] text-fg">
								<Icon
									as={ok ? CheckCircle2 : Circle}
									size={20}
									className={ok ? "text-success-fg" : "text-fg-muted"}
								/>
								<span>{t(`driver.pod.method.${m}`)}</span>
								<span className="sr-only">
									{t(ok ? "driver.pod.captured" : "driver.pod.missing")}
								</span>
							</li>
						);
					})}
				</ul>

				{wantsPhoto && (
					<section className="flex flex-col gap-2" aria-label={t("driver.pod.method.photo")}>
						<p className="text-[14px] font-bold text-fg">{t("driver.pod.method.photo")}</p>
						{photos.length > 0 && (
							<ul className="flex gap-2">
								{photos.map((photo, i) => (
									<li key={draft.photoIds[i] ?? i} className="relative">
										<Thumb photo={photo} alt={t("driver.pod.photoN", { number: i + 1 })} />
										<IconButton
											icon={X}
											size="sm"
											label={t("driver.pod.removePhoto", { number: i + 1 })}
											className="absolute end-0 top-0"
											onClick={() => onRemovePhoto(i)}
										/>
									</li>
								))}
							</ul>
						)}
						{photos.length < MAX_PHOTOS && (
							<CameraCapture
								label={t(photos.length === 0 ? "driver.pod.takePhoto" : "driver.pod.addPhoto")}
								retakeLabel={t("driver.pod.addPhoto")}
								previewLabel={t("driver.pod.photoN", { number: photos.length + 1 })}
								photo={null}
								onCapture={onPhoto}
							/>
						)}
					</section>
				)}

				{methods.includes("signature") && (
					<section className="flex flex-col gap-2" aria-label={t("driver.pod.method.signature")}>
						<p className="text-[14px] font-bold text-fg">{t("driver.pod.method.signature")}</p>
						{signature === null ? (
							<SignaturePad
								label={t("driver.pod.signHere")}
								clearLabel={t("driver.pod.clear")}
								doneLabel={t("driver.pod.signed")}
								hint={t("driver.pod.landscape")}
								onDone={onSignature}
							/>
						) : (
							<>
								<Thumb photo={signature} alt={t("driver.pod.method.signature")} wide />
								<Button onClick={() => onSignature(null)}>{t("driver.pod.signAgain")}</Button>
							</>
						)}
					</section>
				)}

				<Field
					label={t("driver.pod.recipientName")}
					required={draft.fallback}
					optional={!draft.fallback}
				>
					{(props) => (
						<Input
							{...props}
							maxLength={120}
							autoComplete="off"
							value={draft.recipientName}
							onChange={(e) => onChange({ ...draft, recipientName: e.target.value })}
						/>
					)}
				</Field>

				{methods.includes("otp") && <OtpSection stop={stop} draft={draft} onChange={onChange} />}
			</div>
			<ConfirmDialog
				open={asking}
				onOpenChange={setAsking}
				tone="danger"
				title={t("driver.outcome.discardTitle")}
				description={t("driver.pod.discard")}
				confirmLabel={t("driver.outcome.discard")}
				cancelLabel={t("driver.outcome.keepEditing")}
				onConfirm={() => {
					setAsking(false);
					onCancel();
				}}
			/>
		</Drawer>
	);
}

function OtpSection({
	stop,
	draft,
	onChange,
}: {
	stop: DriverShipment;
	draft: PodDraft;
	onChange: (next: PodDraft) => void;
}) {
	const { t } = useI18n();
	const api = useApi();
	const { online } = useOffline();
	const [code, setCode] = useState("");
	const [status, setStatus] = useState<OtpStatus | null>(null);
	const [error, setError] = useState<{ key: string; tries?: number } | null>(null);
	const [wait, setWait] = useState(0);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (wait <= 0) {
			return;
		}
		const tick = setTimeout(() => setWait((s) => s - 1), 1000);
		return () => clearTimeout(tick);
	}, [wait]);

	async function send() {
		setBusy(true);
		setError(null);
		try {
			const next = await api.sendShipmentOtp(stop.shipmentId);
			setStatus(next);
			setWait(RESEND_AFTER_S);
			if (next.verified) {
				onChange({ ...draft, otpVerified: true });
			}
		} catch (e) {
			setError(otpError(e, status));
		} finally {
			setBusy(false);
		}
	}

	async function verify() {
		setBusy(true);
		setError(null);
		try {
			const next = await api.verifyShipmentOtp(stop.shipmentId, code);
			setStatus(next);
			if (next.verified) {
				onChange({ ...draft, otpVerified: true, fallback: false });
			}
		} catch (e) {
			const tries = status === null ? null : { ...status, triesLeft: status.triesLeft - 1 };
			setStatus(tries);
			setError(otpError(e, tries));
			setCode("");
		} finally {
			setBusy(false);
		}
	}

	const locked = status?.locked === true || error?.key === "driver.pod.otp.locked";

	return (
		<section className="flex flex-col gap-3" aria-label={t("driver.pod.method.otp")}>
			<p className="text-[14px] font-bold text-fg">{t("driver.pod.method.otp")}</p>
			{draft.otpVerified ? (
				<p role="status" className="text-[14px] font-bold text-success-fg">
					{t("driver.pod.otp.verified")}
				</p>
			) : !online ? (
				<p
					role="status"
					className="rounded-lg border border-warning-border bg-warning-bg p-3 text-[14px] text-warning-fg"
				>
					{t("driver.pod.otp.offline")}
				</p>
			) : (
				!locked && (
					<>
						<Button size="lg" onClick={() => void send()} disabled={busy || wait > 0}>
							{wait > 0
								? t("driver.pod.otp.resendIn", { seconds: wait })
								: t(status === null ? "driver.pod.otp.send" : "driver.pod.otp.resend", {
										phone: maskPhone(stop.recipientPhone),
									})}
						</Button>
						{status !== null && (
							<>
								<NumericPad
									value={code}
									onChange={setCode}
									maxLength={6}
									label={t("driver.pod.otp.code")}
									deleteLabel={t("driver.pod.otp.delete")}
									disabled={busy}
								/>
								<Button
									variant="primary"
									size="lg"
									disabled={code.length !== 6}
									loading={busy}
									onClick={() => void verify()}
								>
									{t("driver.pod.otp.verify")}
								</Button>
							</>
						)}
					</>
				)
			)}
			{error !== null && (
				<p role="alert" className="text-[13px] text-danger-fg">
					{t(error.key, { tries: error.tries ?? 0 })}
				</p>
			)}
			{!draft.otpVerified && (
				<label className="flex min-h-12 items-center gap-3 rounded-lg border border-border px-3 text-[15px] text-fg has-checked:border-brand has-checked:bg-brand-subtle">
					<input
						type="checkbox"
						className="size-5 accent-brand"
						checked={draft.fallback}
						onChange={(e) => onChange({ ...draft, fallback: e.target.checked })}
					/>
					{t("driver.pod.otp.fallback")}
				</label>
			)}
		</section>
	);
}

/** A captured photo or signature, from its bytes. */
function Thumb({ photo, alt, wide = false }: { photo: Blob; alt: string; wide?: boolean }) {
	const [url, setUrl] = useState<string | null>(null);
	useEffect(() => {
		if (typeof URL.createObjectURL !== "function") {
			return;
		}
		const next = URL.createObjectURL(photo);
		setUrl(next);
		return () => URL.revokeObjectURL(next);
	}, [photo]);
	const size = wide ? "h-24 w-full bg-white object-contain" : "size-20 object-cover";
	return url === null ? (
		<span role="img" aria-label={alt} className={`block rounded-md bg-bg-subtle ${size}`} />
	) : (
		<img src={url} alt={alt} className={`rounded-md border border-border ${size}`} />
	);
}
